/**
 * Solar Cooling Quiz — Product Configuration Calculator v2
 * ES6+ — no var. Uses question_count for column detection.
 * Renders HTML matching .solar-quiz__* / .quiz-* CSS classes.
 *
 * v2 CHANGES:
 *  1. goNext() skips N/A-only columns AND never auto-skips steps that have
 *     a split-options gate (lets the user decide even when CSV has only N/A).
 *  2. Custom options are filtered against CSV-available values so bizarre
 *     mixes (voltages + distances in one step) don't show.
 *  3. New "split question" support: one block can render as a Yes/No gate
 *     followed by a follow-up question stored in the same CSV column.
 *  4. goBack() clears split state for erased steps.
 *  5. FIX: goNext() final condition — split gate steps with empty CSV columns
 *     now render instead of jumping to results.
 *  6. FIX: renderStep() initial check — considers custom options & split gates
 *     before aborting to results.
 */

/* ── SVG icons inline ── */
const SVG = {
  sun: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M1 12h2m16 0h2M4.22 19.78l1.42-1.42m12.72-12.72 1.42-1.42"/></svg>',
  battery: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="16" height="10" rx="2" ry="2"/><path d="M22 11v2M6 7v10m4-10v10m4-10v10"/></svg>',
  grid: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
  home: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></svg>',
  check: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  mail: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>',
  pkg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="m21 16-9-5.04L3 16"/><path d="M3 16v-8a2 2 0 0 1 1.08-1.78l7-3.47a2 2 0 0 1 1.84 0l7 3.47A2 2 0 0 1 21 8v8a2 2 0 0 1-1.08 1.78l-7 3.47a2 2 0 0 1-1.84 0l-7-3.47A2 2 0 0 1 3 16z"/><path d="M12 11.5V22"/></svg>',
  search: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>',
  close: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  tag: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>',
};
const STEP_ICONS = ['sun', 'home', 'grid', 'battery', 'sun', 'home', 'grid', 'battery', 'sun', 'home', 'grid', 'battery', 'sun', 'home', 'grid', 'battery', 'sun', 'home', 'grid', 'battery', 'sun', 'home', 'grid', 'battery', 'sun'];

/* ── helpers ── */
const esc = (t) => { const d = document.createElement('div'); d.textContent = String(t); return d.innerHTML; };
const eqNA = (a, b) => a === b || a === 'N/A' || b === 'N/A';

/* ── CSV parser ── */
function parseCSV(text) {
  const rows = [];
  let row = [], val = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (q) {
      if (c === '"') { if (n === '"') { val += '"'; i++; } else q = false; }
      else val += c;
    } else {
      if (c === '"') q = true;
      else if (c === ',') { row.push(val.trim()); val = ''; }
      else if (c === '\n' || c === '\r') { if (c === '\r' && n === '\n') i++; row.push(val.trim()); if (row.length > 1 || row[0]) rows.push(row); row = []; val = ''; }
      else val += c;
    }
  }
  row.push(val.trim());
  if (row.length > 1 || row[0]) rows.push(row);
  return rows;
}

/* ═══════════════════════════════════════════════════════════════════════
   initSolarQuiz — mounts into #solar-quiz-app-{id}
   ═══════════════════════════════════════════════════════════════════════ */
function initSolarQuiz(root) {
  if (!root) return;
  const sectionId = root.dataset.sectionId || '';
  const app = root.querySelector(`#solar-quiz-app-${sectionId}`);
  const errorBox = root.querySelector(`#solar-quiz-error-${sectionId}`);
  if (!app) return;

  /* ── config ── */
  const cfg = {
    csvUrl: root.dataset.csvUrl || '',
    collectionHandle: root.dataset.collectionHandle || 'all',
    n8nWebhookUrl: root.dataset.n8nWebhookUrl || '',
    calculatorUrl: root.dataset.calculatorUrl || '',
    quizType: root.dataset.quizType || '',
    questionCount: parseInt(root.dataset.questionCount || '0', 10),
    unknownStep: parseInt(root.dataset.unknownOptionStep || '-1', 10),
    labelUnknown: root.dataset.labelUnknown || 'Je ne sais pas (Calculateur)',
    debugMode: root.dataset.debugMode === 'true',
    loading: root.dataset.loadingText || 'Chargement...',
    back: root.dataset.backBtnText || 'Retour',
    next: root.dataset.nextBtnText || 'Suivant',
    restart: root.dataset.restartBtnText || 'Recommencer',
    addCart: root.dataset.addToCartBtnText || 'Ajouter tout au panier',
    emailResults: root.dataset.emailMyResultsBtnText || 'Envoyer par courriel',
    send: root.dataset.sendPdfBtnText || 'Envoyer',
    skip: root.dataset.skipEmailBtnText || 'Fermer',
    resultHeading: root.dataset.resultHeading || 'Votre configuration recommandee',
    resultSubheading: root.dataset.resultSubheading || 'This recommendation is based on the information you provided and Solar Cooling sizing guidelines for both heat pump capacity and solar power requirements. Final equipment selection should always be confirmed by a qualified installer.',
    emailHeading: root.dataset.emailFormHeading || 'Recevez vos resultats par courriel',
    emailSubtitle: root.dataset.emailFormSubtitle || 'Entrez votre courriel pour recevoir votre configuration recommandee.',
    emailSuccess: root.dataset.emailSuccessMessage || 'Vos resultats ont ete envoyes.',
    emailError: root.dataset.emailErrorMessage || 'Impossible d\'envoyer le formulaire pour le moment.',
    noMatch: root.dataset.noMatchText || 'Aucune configuration ne correspond a ces reponses.',
    multiMatch: root.dataset.multipleMatchText || 'Plusieurs configurations correspondent. Veuillez contacter notre equipe.',
    noProducts: root.dataset.noProductsText || 'Aucun produit n\'a ete trouve pour cette configuration.',
    unresolved: root.dataset.unresolvedVariantText || 'Reference non associee a un variant Shopify.',
    cartError: root.dataset.addToCartErrorText || 'Impossible d\'ajouter les produits au panier.',
    fieldError: root.dataset.requiredFieldErrorText || 'Veuillez remplir ce champ.',
    labelFN: root.dataset.labelFirstName || 'Prenom',
    labelLN: root.dataset.labelLastName || 'Nom',
    labelEM: root.dataset.labelEmail || 'Courriel',
    phFN: root.dataset.placeholderFirstName || 'Votre prenom',
    phLN: root.dataset.placeholderLastName || 'Votre nom',
    phEM: root.dataset.placeholderEmail || 'votre@courriel.com',
      };

  /* Block labels: newline-separated */
  const blockLabels = (root.dataset.questionLabels || '').split('\n').map(l => l.trim());

  /* Block options: "idx|value|label" */
  const blockOptions = {};
  (root.dataset.questionOptions || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 3) {
      const idx = parseInt(p[0], 10);
      if (!blockOptions[idx]) blockOptions[idx] = [];
      blockOptions[idx].push({ value: p[1], label: p[2] });
    }
  });

  /* Block hints: "idx|hint_text" */
  const blockHints = {};
  (root.dataset.questionHints || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 2) {
      const idx = parseInt(p[0], 10);
      blockHints[idx] = p.slice(1).join('|');
    }
  });

  /* Block label conditions: "idx|trigger_step|trigger_answer|alternate_label" */
  const blockLabelConditions = {};
  (root.dataset.questionLabelConditions || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 4) {
      const idx = parseInt(p[0], 10);
      if (!blockLabelConditions[idx]) blockLabelConditions[idx] = [];
      blockLabelConditions[idx].push({
        triggerStep: parseInt(p[1], 10),
        triggerAnswer: p[2],
        alternateText: p.slice(3).join('|')
      });
    }
  });

  /* Block hint conditions: "idx|trigger_step|trigger_answer|alternate_hint" */
  const blockHintConditions = {};
  (root.dataset.questionHintConditions || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 4) {
      const idx = parseInt(p[0], 10);
      if (!blockHintConditions[idx]) blockHintConditions[idx] = [];
      blockHintConditions[idx].push({
        triggerStep: parseInt(p[1], 10),
        triggerAnswer: p[2],
        alternateText: p.slice(3).join('|')
      });
    }
  });

  /* ═══════════════════════════════════════════════════════════════════
     v2: Split-question support (gate question → follow-up in same col)
     Format from dataset: "idx|trigger_value|follow_up_label"
     Follow-up options are stored separately from gate options.
     ═══════════════════════════════════════════════════════════════════ */
  const blockSplitTriggers = {};   // idx → trigger value (e.g. "Oui")
  const blockSplitLabels = {};     // idx → follow-up label
  const blockSplitHints = {};      // idx → follow-up hint
  const blockSplitOptions = {};    // idx → [{value, label}, ...] follow-up options
  (root.dataset.questionSplitTriggers || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 3) {
      const idx = parseInt(p[0], 10);
      blockSplitTriggers[idx] = p[1];
      blockSplitLabels[idx] = p.slice(2).join('|');
    }
  });
  (root.dataset.questionSplitHints || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 2) {
      const idx = parseInt(p[0], 10);
      blockSplitHints[idx] = p.slice(1).join('|');
    }
  });
  (root.dataset.questionSplitOptions || '').split('\n').forEach(line => {
    const p = line.trim().split('|');
    if (p.length >= 3) {
      const idx = parseInt(p[0], 10);
      if (!blockSplitOptions[idx]) blockSplitOptions[idx] = [];
      blockSplitOptions[idx].push({ value: p[1], label: p[2] });
    }
  });

  /* ── state ── */
  const S = { rows: [], hdrs: [], skuMap: {}, answers: {}, history: [], step: 0, products: [], diagram: null, splitState: {} };

  /* question columns: first questionCount columns */
  const qCols = Array.from({ length: cfg.questionCount }, (_, i) => i);

  /* Helper: does a step have an active split gate? */
  function hasSplitGate(stepIdx) {
    return blockSplitTriggers[stepIdx] && !S.splitState[stepIdx];
  }

  /* ── conditional label/hint helpers ── */
  function checkCondition(condition) {
    if (!condition) return null;
    const actualAnswer = S.answers[qCols[condition.triggerStep]];
    if (actualAnswer !== undefined && eqNA(actualAnswer, condition.triggerAnswer)) {
      return condition.alternateText;
    }
    return null;
  }

  function resolveConditional(stepIdx, conditionsMap, fallbackMap) {
    const conditions = conditionsMap[stepIdx];
    if (conditions && conditions.length) {
      for (const cond of conditions) {
        const result = checkCondition(cond);
        if (result !== null) return result;
      }
    }
    return (fallbackMap && fallbackMap[stepIdx]) || '';
  }

  /* ═══════════════════════════  DATA  ═══════════════════════════ */

  async function loadCSV() {
    if (!cfg.csvUrl) throw new Error('Aucun fichier CSV configure.');
    const r = await fetch(cfg.csvUrl);
    if (!r.ok) throw new Error(`Erreur CSV (${r.status}). Verifiez que le fichier est bien televerse dans Shopify > Contenu > Fichiers.`);
    const all = parseCSV(await r.text());
    if (all.length < 2) throw new Error('CSV vide.');
    S.hdrs = all[0];
    S.rows = all.slice(1).filter(row => row.some(c => c && c.trim() !== ''));
  }

  async function loadSkuMap() {
    const el = document.getElementById(`solar-quiz-sku-variant-map-${sectionId}`);
    if (el) { try { S.skuMap = JSON.parse(el.textContent) || {}; return; } catch { /* fallback */ } }
    try {
      const r = await fetch(`/collections/${cfg.collectionHandle}/products.json?limit=250`);
      if (!r.ok) return;
      const data = await r.json();
      (data.products || []).forEach(p => p.variants.forEach(v => {
        if (v.sku) S.skuMap[v.sku] = { variantId: v.id, title: p.title, productUrl: `/products/${p.handle}`, price: v.price || '', imageUrl: p.images?.[0]?.src || p.image?.src || '' };
      }));
    } catch (e) { console.warn('[Quiz] SKU fetch failed:', e); }
  }

  /* ═══════════════════════════  LOGIC  ═══════════════════════════ */

  function findSkuPairs() {
    const pairs = [];
    for (let i = cfg.questionCount; i < S.hdrs.length - 1; i++) {
      const h = (S.hdrs[i] || '').trim();
      const n = (S.hdrs[i + 1] || '').trim();
      if (h.startsWith('SKU') && (n === 'QTY' || n.startsWith('QTY'))) pairs.push({ skuCol: i, qtyCol: i + 1 });
    }
    return pairs;
  }

  function matchedRows() {
    return S.rows.filter(row => qCols.every(qc => {
      const ans = S.answers[qc];
      if (ans === undefined || ans === null || ans === '') return true;
      return eqNA(ans, row[qc]);
    }));
  }

  function availableOptions(stepIdx) {
    if (stepIdx >= qCols.length) return [];
    const matched = matchedRows();
    const vals = new Set();
    matched.forEach(row => {
      const v = row[qCols[stepIdx]];
      if (v && v.trim() && v.trim() !== 'N/A') vals.add(v.trim());
    });
    return [...vals].sort();
  }

  function getLabel(stepIdx, isSplitFollowUp) {
    /* v2: follow-up label override */
    if (isSplitFollowUp && blockSplitLabels[stepIdx]) return blockSplitLabels[stepIdx];
    /* Check conditional labels first */
    const conditionalLabel = resolveConditional(stepIdx, blockLabelConditions, {});
    if (conditionalLabel) return conditionalLabel;
    /* Fall back to block label or CSV header */
    if (blockLabels[stepIdx]) return blockLabels[stepIdx];
    const h = (S.hdrs[qCols[stepIdx]] || '').trim();
    return h || `Question ${stepIdx + 1}`;
  }

  function getHint(stepIdx, isSplitFollowUp) {
    /* v2: follow-up hint override */
    if (isSplitFollowUp && blockSplitHints[stepIdx]) return blockSplitHints[stepIdx];
    return resolveConditional(stepIdx, blockHintConditions, blockHints);
  }

  function getOptionDisplayLabel(stepIdx, value) {
    const opts = blockOptions[stepIdx];
    if (opts) {
      const match = opts.find(o => o.value === value);
      if (match) return match.label;
    }
    const splitOpts = blockSplitOptions[stepIdx];
    if (splitOpts) {
      const match = splitOpts.find(o => o.value === value);
      if (match) return match.label;
    }
    return value;
  }

  function extractProducts(row) {
    if (!row) return [];
    const pairs = findSkuPairs();
    const prods = [];
    pairs.forEach(({ skuCol, qtyCol }) => {
      const sku = (row[skuCol] || '').trim();
      const qty = parseInt((row[qtyCol] || '0').trim(), 10);
      if (sku && sku !== 'N/A' && sku !== '' && qty > 0) {
        const info = S.skuMap[sku] || {};
        prods.push({ sku, qty, title: info.title || sku, variantId: info.variantId || null, productUrl: info.productUrl || '#', price: info.price || null, imageUrl: info.imageUrl || null });
      }
    });
    return prods;
  }

  function resolveDiagram(row) {
    if (!row) return null;
    const col = S.hdrs.findIndex(header => header.trim().toLowerCase() === 'diagram_key');
    if (col < cfg.questionCount) return null;

    const key = (row[col] || '').trim();
    const titles = {
      'battery-48v': { en: '48V battery system', fr: 'Système avec batterie 48 V' },
      'hybrid-240v': { en: 'Solar and 240V grid system', fr: 'Système solaire et réseau 240 V' },
      'solar-only': { en: 'Solar-only system', fr: 'Système solaire seulement' },
      'grid-240v': { en: '240V grid system', fr: 'Système réseau 240 V' },
      'solar-duo': { en: 'AC/DC solar duo system', fr: 'Système solaire CA/CC' },
      'grid-120v': { en: '120V grid system', fr: 'Système réseau 120 V' }
    };
    if (!Object.prototype.hasOwnProperty.call(titles, key)) return null;

    const lang = (document.documentElement.lang || '').toLowerCase().startsWith('fr') ? 'fr' : 'en';
    const pdfUrl = root.getAttribute(`data-diagram-${key}-${lang}`);
    const previewUrl = root.getAttribute(`data-diagram-preview-${key}-${lang}`);
    if (!pdfUrl) return null;
    const voltageCol = S.hdrs.indexOf('Voltage');
    const note = key === 'solar-duo' && voltageCol !== -1 && row[voltageCol] === '120V'
      ? (lang === 'fr'
          ? "La configuration 120 V comprend un autotransformateur qui n'est pas illustré sur ce schéma. Faites confirmer le branchement final par votre installateur."
          : 'The 120V configuration includes an autotransformer not shown in this diagram. Have your installer confirm the final wiring.')
      : '';
    return { key, lang, pdfUrl, previewUrl, title: titles[key][lang], note };
  }

  function buildLabeledAnswers() {
    const labeled = {};
    Object.entries(S.answers).forEach(([colIdx, value]) => {
      if (!value || value === 'N/A' || value === '') return;
      const idx = parseInt(colIdx, 10);
      const label = blockLabels[idx] || (S.hdrs[idx] || '').trim() || `Q${idx + 1}`;
      labeled[label] = getOptionDisplayLabel(idx, value);
    });
    return labeled;
  }

  function detectBtuValue() {
    const btuRegex = /^\s*(12000|18000|24000|36000)\s*$/;
    for (const value of Object.values(S.answers)) {
      const v = String(value).trim();
      if (btuRegex.test(v)) return v;
    }
    return '';
  }

  /* ═══════════════════════════  RENDER STEP  ═══════════════════════════ */

  function renderStep(idx, preselectedValue = null) {
    const col = qCols[idx];
    const storedAnswer = S.answers[col];
    const hadStoredAnswer = storedAnswer !== undefined;

    /* Temporarily clear the current answer so availableOptions returns every option for this step */
    if (hadStoredAnswer) delete S.answers[col];
    const opts = availableOptions(idx);
    if (hadStoredAnswer) S.answers[col] = storedAnswer;

    /* ── FIX #2: also check custom options & split options before aborting ── */
    const hasCustomOpts = blockOptions[idx] && blockOptions[idx].length;
    const hasSplitOpts = blockSplitOptions[idx] && blockSplitOptions[idx].length;
    if (!opts.length && !hasSplitGate(idx) && !hasCustomOpts && !hasSplitOpts) { renderResult(); return; }

    /* v2: split mode detection */
    const inSplitMode = S.splitState[idx];
    const label = getLabel(idx, inSplitMode);
    const hint = getHint(idx, inSplitMode);
    const cOpts = blockOptions[idx];           /* gate options (if split) or normal custom options */
    const sOpts = blockSplitOptions[idx];       /* v2: follow-up options for split mode */
    const hasCustom = cOpts && cOpts.length;
    const hasSplit = sOpts && sOpts.length; /* v2 */
    const iconKey = STEP_ICONS[idx % STEP_ICONS.length];
    const pct = qCols.length ? ((idx / qCols.length) * 100) : 0;

    /* Build option cards */
    let cardsHtml = '';
    if (inSplitMode && hasSplit) {
      /* v2: follow-up mode — filter follow-up options against CSV-available values */
      const availableSet = new Set(opts);
      const filteredOpts = sOpts.filter(o => availableSet.has(o.value));
      cardsHtml = filteredOpts.length
        ? filteredOpts.map(o => cardHtml(o.value, o.label, '')).join('')
        : sOpts.map(o => cardHtml(o.value, o.label, '')).join('');
    } else if (hasSplitGate(idx) && hasCustom) {
      /* v2: split gate mode — show gate options UNFILTERED (UI-only) */
      cardsHtml = cOpts.map(o => cardHtml(o.value, o.label, '')).join('');
    } else if (hasCustom) {
      /* v2: normal custom options — filter against CSV-available values */
      const availableSet = new Set(opts);
      const filteredOpts = cOpts.filter(o => availableSet.has(o.value));
      cardsHtml = filteredOpts.length
        ? filteredOpts.map(o => cardHtml(o.value, o.label, '')).join('')
        : opts.map(v => cardHtml(v, v, '')).join('');
    } else {
      cardsHtml = opts.map(v => cardHtml(v, v, '')).join('');
    }

    /* "I don't know" card */
    if (cfg.unknownStep === idx && cfg.calculatorUrl) {
      cardsHtml += `<button type="button" class="quiz-option-card quiz-option-card--unknown" data-calculator>
        <span class="quiz-option-card__icon">${SVG.search}</span>
        <span class="quiz-option-card__label">${esc(cfg.labelUnknown)}</span>
      </button>`;
    }

    const hintHtml = hint ? `<p class="quiz-step__hint">${esc(hint)}</p>` : '';

    app.innerHTML = `
      <div class="solar-quiz__step-wrapper">
        <div class="quiz-progress">
          <span class="quiz-progress__label">${idx + 1} / ${qCols.length}</span>
          <div class="quiz-progress__track">
            <div class="quiz-progress__fill" style="width:${pct}%"></div>
          </div>
        </div>
        <div class="quiz-step__header">
          <!-- <div class="quiz-step__icon" aria-hidden="true">${SVG[iconKey] || SVG.sun}</div> -->
          <h2 class="quiz-step__question">${esc(label)}</h2>
          ${hintHtml}
        </div>
        <div class="quiz-card-grid" role="radiogroup" aria-label="${esc(label)}">
          ${cardsHtml}
        </div>
        <div class="quiz-nav">
          ${idx > 0 ? `<button type="button" class="btn btn--quiz-secondary" data-action="back">${esc(cfg.back)}</button>` : '<span></span>'}
          <button type="button" class="btn btn--quiz-primary" data-action="next" disabled>${esc(cfg.next)}</button>
        </div>
      </div>`;

    bindStepEvents(idx, preselectedValue);
  }

  function cardHtml(value, displayLabel, sublabel) {
    const subHtml = sublabel ? `<span class="quiz-option-card__sublabel">${esc(sublabel)}</span>` : '';
    return `<button type="button" class="quiz-option-card" data-value="${esc(value)}">
      <span class="quiz-option-card__label">${esc(displayLabel)}</span>
      ${subHtml}
    </button>`;
  }

  function bindStepEvents(idx, preselectedValue = null) {
    const wrap = app.querySelector('.solar-quiz__step-wrapper');
    const col = qCols[idx];
    const splitTrigger = blockSplitTriggers[idx];
    const inSplitMode = S.splitState[idx];

    /* Option selection */
    wrap.querySelectorAll('.quiz-option-card[data-value]').forEach(card => {
      card.addEventListener('click', () => {
        /* v2: split gate handling */
        if (splitTrigger && !inSplitMode) {
          if (card.dataset.value === splitTrigger) {
            /* Trigger matched: enter follow-up mode */
            S.splitState[idx] = true;
            renderStep(idx);
            return;
          }
          /* Non-trigger answer: store as N/A for the column, advance */
          S.answers[col] = 'N/A';
          goNext();
          return;
        }

        /* Normal selection */
        wrap.querySelectorAll('.quiz-option-card[data-value]').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        S.answers[col] = card.dataset.value;
        wrap.querySelector('[data-action="next"]').disabled = false;
      });
    });

    /* Calculator link */
    const calcBtn = wrap.querySelector('[data-calculator]');
    if (calcBtn) calcBtn.addEventListener('click', () => { if (cfg.calculatorUrl) window.open(cfg.calculatorUrl, '_blank'); });

    /* Nav */
    wrap.querySelector('[data-action="back"]')?.addEventListener('click', goBack);
    wrap.querySelector('[data-action="next"]').addEventListener('click', goNext);

    /* Keyboard */
    const cards = [...wrap.querySelectorAll('.quiz-option-card[data-value]')];
    cards.forEach((card, i) => {
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
        else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); cards[(i + 1) % cards.length]?.focus(); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); cards[(i - 1 + cards.length) % cards.length]?.focus(); }
      });
    });

    /* Restore selection */
    const saved = preselectedValue !== null ? preselectedValue : S.answers[col];
    if (saved !== undefined) {
      const hasMatch = cards.some(c => {
        if (c.dataset.value === saved) {
          c.classList.add('is-selected');
          return true;
        }
        return false;
      });
      if (hasMatch) {
        wrap.querySelector('[data-action="next"]').disabled = false;
      }
    }
  }

  /* ═══════════════════════════  RENDER RESULT  ═══════════════════════════ */

  function renderResult() {
    const row = matchedRows()[0];
    S.products = extractProducts(row);
    S.diagram = resolveDiagram(row);

    if (!S.products.length) {
      app.innerHTML = `
        <div class="solar-quiz__step-wrapper solar-quiz__step-wrapper--result">
          <div class="quiz-empty-state">
            <div class="quiz-empty-state__icon">${SVG.search}</div>
            <h3 class="quiz-empty-state__title">${esc(cfg.noMatch)}</h3>
            <p class="quiz-empty-state__text">${esc(cfg.noProducts)}</p>
            <button type="button" class="btn btn--quiz-secondary" data-action="restart">${esc(cfg.restart)}</button>
          </div>
        </div>`;
      app.querySelector('[data-action="restart"]').addEventListener('click', reset);
      if (cfg.debugMode) renderDebug();
      return;
    }

    /* Summary pills */
    const summaryHtml = Object.entries(S.answers)
      .filter(([, v]) => v && v !== 'N/A')
      .map(([k, v]) => {
        const idx = parseInt(k, 10);
        const rawLbl = (blockLabels[idx] || (S.hdrs[idx] || '').trim() || `Q${idx + 1}`).trim();
        const lbl = rawLbl || `Q${idx + 1}`;
        const displayValue = getOptionDisplayLabel(idx, v);
        return `<span class="quiz-result__summary-item">${SVG.tag} <span class="quiz-result__summary-text"><span class="quiz-result__summary-label">${esc(lbl)}:</span> <span class="quiz-result__summary-value">${esc(displayValue)}</span></span></span>`;
      }).join('');

    /* Product cards */
    const prodsHtml = S.products.map(p => {
      const img = p.imageUrl
        ? `<div class="quiz-result-item__thumb-wrap"><img src="${esc(p.imageUrl)}" alt="${esc(p.title)}"></div>`
        : `<div class="quiz-result-item__thumb-wrap"><div class="quiz-result-item__thumb-placeholder">${SVG.pkg}</div></div>`;
      const link = p.productUrl !== '#' ? `<p class="quiz-result-item__link"><a href="${esc(p.productUrl)}" target="_blank">View Product</a></p>` : '';
      const unresolved = !p.variantId ? `<p class="quiz-result-item__unresolved">${esc(cfg.unresolved)}</p>` : '';
      return `<div class="quiz-result-item">
        ${img}
        <div class="quiz-result-item__info">
          <h3 class="quiz-result-item__title"><a href="${esc(p.productUrl)}" target="_blank">${p.title}</a></h3>
          <p class="quiz-result-item__sku">${esc(p.sku)}</p>
          ${link}
          ${unresolved}
        </div>
        <span class="quiz-result-item__qty">${p.qty}</span>
      </div>`;
    }).join('');

    const diagram = S.diagram;
    const diagramHtml = diagram ? `
      <section class="quiz-result__diagram" aria-label="${esc(diagram.title)}">
        <div class="quiz-result__diagram-copy">
          <h3 class="quiz-result__diagram-heading">${diagram.lang === 'fr' ? "Schéma d'installation" : 'Installation diagram'}</h3>
          <p class="quiz-result__diagram-title">${esc(diagram.title)}</p>
        </div>
        <div class="quiz-result__diagram-preview">
          ${diagram.previewUrl ? `<a class="quiz-result__diagram-image" href="${esc(diagram.pdfUrl)}" target="_blank" rel="noopener noreferrer" aria-label="${esc(diagram.lang === 'fr' ? `Ouvrir le schéma : ${diagram.title}` : `Open diagram: ${diagram.title}`)}">
            <img src="${esc(diagram.previewUrl)}" alt="${esc(diagram.title)}" loading="lazy" decoding="async">
          </a>` : ''}
          <iframe class="quiz-result__diagram-pdf" title="${esc(diagram.title)}" loading="lazy" hidden></iframe>
        </div>
        <a class="quiz-result__diagram-link" href="${esc(diagram.pdfUrl)}" target="_blank" rel="noopener noreferrer">${diagram.lang === 'fr' ? 'Voir le schéma complet (PDF)' : 'View full diagram (PDF)'}</a>
        ${diagram.note ? `<p class="quiz-result__diagram-note">${esc(diagram.note)}</p>` : ''}
      </section>` : '';

    /* Actions */
    const emailBtn = cfg.n8nWebhookUrl
      ? `<button type="button" class="btn btn--quiz-primary" data-action="email">${esc(cfg.emailResults)}</button>`
      : '';
    const orSpan = cfg.n8nWebhookUrl ? `<span class="quiz-result__actions-or"></span>` : '';

    app.innerHTML = `
      <div class="solar-quiz__step-wrapper solar-quiz__step-wrapper--result">
        <div class="quiz-result__header">
          <div class="quiz-result__icon">${SVG.check}</div>
          <h2 class="quiz-result__heading">${esc(cfg.resultHeading)}</h2>
          <p class="quiz-result__subheading">${esc(cfg.resultSubheading)}</p>
        </div>
        <div class="quiz-result__summary">${summaryHtml}</div>
        ${diagramHtml}
        <div class="quiz-result__products">${prodsHtml}</div>
        <div class="quiz-result__actions">
          <div class="quiz-result__actions-row">
            <button type="button" class="btn btn--quiz-blue" data-action="cart">${esc(cfg.addCart)}</button>
            ${orSpan}
            ${emailBtn}
          </div>
          <button type="button" class="btn btn--quiz-ghost" data-action="restart">${esc(cfg.restart)}</button>
        </div>
      </div>`;

    app.querySelector('[data-action="cart"]')?.addEventListener('click', addAllToCart);
    app.querySelector('[data-action="email"]')?.addEventListener('click', openEmailModal);
    app.querySelector('[data-action="restart"]')?.addEventListener('click', reset);
    const diagramImage = app.querySelector('.quiz-result__diagram-image');
    const diagramPreview = diagramImage?.querySelector('img');
    const pdfPreview = app.querySelector('.quiz-result__diagram-pdf');
    if (diagram && pdfPreview) {
      const showPdfPreview = () => {
        if (diagramImage) diagramImage.hidden = true;
        pdfPreview.hidden = false;
        pdfPreview.src = `${diagram.pdfUrl}#page=1&view=FitH&toolbar=0`;
      };
      if (!diagramPreview || (diagramPreview.complete && !diagramPreview.naturalWidth)) {
        showPdfPreview();
      } else {
        diagramPreview.addEventListener('error', showPdfPreview, { once: true });
      }
    }
    if (cfg.debugMode) renderDebug();
  }

  /* ═══════════════════════════  CART  ═══════════════════════════ */

  async function addAllToCart() {
    const btn = app.querySelector('[data-action="cart"]');
    if (!btn) return;
    btn.disabled = true;
    const items = S.products.filter(p => p.variantId).map(p => ({ id: p.variantId, quantity: p.qty }));
    if (!items.length) { alert(cfg.unresolved); btn.disabled = false; return; }

    const cartDrawer = document.querySelector('cart-drawer');
    let sections = 'cart-icon-bubble';
    if (cartDrawer) {
      sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error((typeof data.description === 'string' ? data.description : data.message) || cfg.cartError);
      }

      const afterAtc = window.theme?.settings?.afterAtc;
      if (afterAtc === 'page') {
        setTimeout(() => { window.location.href = window.theme?.routes?.cart || '/cart'; }, 300);
        return;
      }

      // Update cart icon bubble
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }

      // Refresh and optionally open the cart drawer
      if (cartDrawer) {
        cartDrawer.renderContents(data, afterAtc === 'drawer');
      }
    } catch (e) {
      console.error('[Quiz] Add to cart failed:', e);
      alert(cfg.cartError);
    } finally {
      btn.disabled = false;
    }
  }

  /* ═══════════════════════════  EMAIL MODAL  ═══════════════════════════ */

  function openEmailModal() {
    closeModal();
    const overlay = document.createElement('div');
    overlay.className = 'quiz-modal-overlay';
    overlay.id = 'quiz-modal-active';
    overlay.innerHTML = `
      <div class="quiz-modal">
        <button type="button" class="quiz-modal__close" data-close>${SVG.close}</button>
        <div class="quiz-modal__header">
          <div class="quiz-modal__icon">${SVG.mail}</div>
          <h3 class="quiz-modal__title">${esc(cfg.emailHeading)}</h3>
          <p class="quiz-modal__subtitle">${esc(cfg.emailSubtitle)}</p>
        </div>
        <div class="quiz-modal__body">
          <form class="quiz-modal__form" onsubmit="return false;">
            <div class="quiz-modal__form-row">
              <div class="quiz-modal__field">
                <label>${esc(cfg.labelFN)}</label>
                <input type="text" class="input" name="firstName" placeholder="${esc(cfg.phFN)}" required>
              </div>
              <div class="quiz-modal__field">
                <label>${esc(cfg.labelLN)}</label>
                <input type="text" class="input" name="lastName" placeholder="${esc(cfg.phLN)}" required>
              </div>
            </div>
            <div class="quiz-modal__field">
              <label>${esc(cfg.labelEM)}</label>
              <input type="email" class="input" name="email" placeholder="${esc(cfg.phEM)}" required>
            </div>
            <div class="quiz-modal__field quiz-modal__hp" style="position:absolute;opacity:0;pointer-events:none;height:0;width:0;overflow:hidden;" aria-hidden="true">
              <label for="quiz-hp-website">Website</label>
              <input type="text" id="quiz-hp-website" name="website" tabindex="-1" autocomplete="off">
            </div>
            <div class="quiz-modal__actions">
              <button type="submit" class="btn btn--quiz-blue">${esc(cfg.send)}</button>
              <div class="quiz-modal__skip">
                <button type="button" class="btn btn--quiz-ghost" data-close>${esc(cfg.skip)}</button>
              </div>
            </div>
            <div class="quiz-modal__status" style="display:none"></div>
          </form>
        </div>
      </div>`;

    document.body.appendChild(overlay);
    overlay.querySelectorAll('[data-close]').forEach(b => b.addEventListener('click', closeModal));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });

    const form = overlay.querySelector('.quiz-modal__form');
    form.addEventListener('submit', (e) => { e.preventDefault(); submitEmail(form); });
  }

  async function submitEmail(form) {
    const btn = form.querySelector('button[type="submit"]');
    const status = form.querySelector('.quiz-modal__status');
    const fd = new FormData(form);
    const email = fd.get('email')?.toString().trim();
    const firstName = fd.get('firstName')?.toString().trim() || '';
    const lastName = fd.get('lastName')?.toString().trim() || '';
    const hp = fd.get('website')?.toString().trim() || '';
    if (!email) return;

    // Honeypot: silently discard bots that filled the hidden field
    if (hp) {
      status.style.display = 'block';
      status.className = 'quiz-modal__status quiz-modal__status--success';
      status.textContent = cfg.emailSuccess;
      form.reset();
      setTimeout(closeModal, 1500);
      return;
    }

    btn.disabled = true;
    status.style.display = 'block';
    status.className = 'quiz-modal__status quiz-modal__status--loading';
    status.textContent = 'Envoi en cours...';

    const payload = {
      email, firstName, lastName, fullName: `${firstName} ${lastName}`.trim(),
      locale: document.documentElement.lang || 'fr',
      systemType: cfg.quizType,
      answers: S.answers, answersLabeled: buildLabeledAnswers(), btuValue: detectBtuValue(), products: S.products,
      diagram: S.diagram ? { key: S.diagram.key, pdfUrl: S.diagram.pdfUrl } : null,
      shopUrl: window.location.hostname,
      cartPrefill: { length: S.products.length, products: S.products.map(p => ({ id: p.variantId, quantity: p.qty, imageUrl: p.imageUrl })) },
      timestamp: new Date().toISOString()
    };

    try {
      const r = await fetch(cfg.n8nWebhookUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const res = await r.json().catch(() => ({}));
      if (r.ok && res.success !== false) {
        status.className = 'quiz-modal__status quiz-modal__status--success';
        status.textContent = res.message || cfg.emailSuccess;
        form.reset();
      } else throw new Error(res.message || 'Failed');
    } catch (e) {
      status.className = 'quiz-modal__status quiz-modal__status--error';
      status.textContent = cfg.emailError;
    } finally {
      btn.disabled = false;
    }
  }

  function closeModal() {
    const el = document.getElementById('quiz-modal-active');
    if (!el) return;
    el.classList.add('is-closing');
    setTimeout(() => el.remove(), 250);
  }

  /* ═══════════════════════════  DEBUG  ═══════════════════════════ */

  function renderDebug() {
    const existing = app.parentElement.querySelector('.quiz-debug-panel');
    if (existing) existing.remove();
    if (!cfg.debugMode) return;

    const csvSkus = new Set();
    S.rows.forEach(row => findSkuPairs().forEach(({ skuCol }) => { const s = (row[skuCol] || '').trim(); if (s && s !== 'N/A') csvSkus.add(s); }));
    const missing = [...csvSkus].filter(s => !S.skuMap[s]);

    const panel = document.createElement('div');
    panel.className = 'quiz-debug-panel';
    panel.innerHTML = `
      <div class="quiz-debug-header">
        <span class="quiz-debug-badge">DEBUG MODE</span>
        <button type="button" class="quiz-debug-close">&times;</button>
      </div>
      <div class="quiz-debug-content">
        <div class="quiz-debug-section">
          <h4>SKUs manquants (CSV mais pas collection)</h4>
          <ul class="quiz-debug-missing-skus">
            ${missing.length ? missing.map(s => `<li>${esc(s)}</li>`).join('') : '<li style="background:#dcfce7;color:#166534">Tous les SKUs sont dans la collection!</li>'}
          </ul>
        </div>
        <div class="quiz-debug-section">
          <h4>Statistiques</h4>
          <pre class="quiz-debug-stats">${esc(['Rows: ' + S.rows.length, 'SKUs CSV: ' + csvSkus.size, 'Mapped: ' + [...csvSkus].filter(s => S.skuMap[s]).length, 'Missing: ' + missing.length, 'Questions: ' + qCols.length, 'Collection: ' + cfg.collectionHandle].join('\n'))}</pre>
        </div>
      </div>`;
    panel.querySelector('.quiz-debug-close').addEventListener('click', () => panel.remove());
    app.parentElement.insertBefore(panel, app.nextSibling);
  }

  /* ═══════════════════════════  NAV  ═══════════════════════════ */

  /* v2: goNext skips N/A columns, but NEVER skips steps that have a split gate */
  function goNext() {
    const col = qCols[S.step];
    if (S.answers[col] === undefined && !S.splitState[S.step]) return;
    S.history.push(S.step);
    S.step++;
    while (S.step < qCols.length) {
  const nextOpts = availableOptions(S.step);
  if (nextOpts.length) break;              // ← Only stops when CSV has values
  S.answers[qCols[S.step]] = 'N/A';
  S.step++;
}
    /* ── FIX #1: check split gate BEFORE availableOptions.length ── */
    if (S.step >= qCols.length) {
      renderResult();
    } else if (blockSplitTriggers[S.step] || availableOptions(S.step).length) {
      renderStep(S.step);
    } else {
      renderResult();
    }
    scrollToActiveStep();
  }

  function scrollToActiveStep() {
    const wrapper = app.querySelector('.solar-quiz__step-wrapper');
    if (!wrapper) return;
    const offset = 20;
    const top = wrapper.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  /* v2: goBack clears split state for erased steps */
  function goBack() {
    if (!S.history.length) return;
    const prev = S.history.pop();
    for (let i = S.step; i > prev; i--) {
      delete S.answers[qCols[i]];
      delete S.splitState[i];  /* v2 */
    }
    S.step = prev;
    renderStep(S.step);
    scrollToActiveStep();
  }

  function reset() {
    S.answers = {}; S.history = []; S.step = 0; S.products = []; S.diagram = null; S.splitState = {};
    renderStep(0);
    scrollToActiveStep();
  }

  /* ═══════════════════════════  INIT  ═══════════════════════════ */

  async function init() {
    app.innerHTML = `<div class="quiz-loading"><div class="quiz-loading__spinner"></div><p>${esc(cfg.loading)}</p></div>`;
    try {
      await Promise.all([loadCSV(), loadSkuMap()]);
      if (!cfg.questionCount || cfg.questionCount < 1) throw new Error('question_count invalide.');
      if (!S.rows.length) throw new Error('CSV vide.');
      renderStep(0);
    } catch (e) {
      console.error('[Quiz]', e);
      if (errorBox) { errorBox.hidden = false; errorBox.textContent = e.message; }
      app.innerHTML = `<div class="quiz-empty-state">
        <div class="quiz-empty-state__icon">${SVG.search}</div>
        <h3 class="quiz-empty-state__title">${esc(cfg.loading)}</h3>
        <p class="quiz-empty-state__text">${esc(e.message)}</p>
        <button type="button" class="btn btn--quiz-secondary" onclick="location.reload()">Reessayer</button>
      </div>`;
    }
  }

  init();
}

/* ── Auto-init ── */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.solar-quiz').forEach(el => initSolarQuiz(el));
});
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', e => { const el = e.target.querySelector('.solar-quiz'); if (el) initSolarQuiz(el); });
}
